import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  createDepartment,
  deleteDepartment,
  updateDepartment,
} from '@/entities/department/api';
import type {Department} from '@/entities/department/types';
import {fieldConflict, messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {AppInput} from '@/shared/ui/input';
import {ChoiceChips} from '@/shared/ui/segmented';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';
import {
  DEFAULT_DEPARTMENT_TYPE,
  DEPARTMENT_TYPES,
  departmentTypeLabelKey,
} from '../lib/departmentType';

/**
 * Create / edit a department in a bottom-anchored modal. Edit mode is entered with an
 * existing `department`; it adds a destructive Delete with a two-tap confirm (a mis-tap
 * must not erase a unit that people and devices are filed under). On success it
 * invalidates the company's department list so the row updates without a manual refresh.
 *
 * ## Что на самом деле делает «Удалить»
 *
 * НЕ расформировывает. `DELETE /departments/{id}` удаляет отдел в
 * `sv_organization`; в `sv_control`, где живёт проекция, строка не удаляется —
 * очередной проход помечает её `is_active = false` («never delete: device/
 * personnel links must survive», `dept_projection.rs:228`). Отсюда три факта,
 * которых по коду кабинета не видно (`cabinet-frontend-guide.md` §4, врезка
 * «Удаление отдела»):
 *
 *   - привязки людей переживают удаление — `personnel_departments` не трогают;
 *   - терминал, стоявший в отделе, продолжает пускать ТЕХ ЖЕ людей: разрешение
 *     скоупа на `is_active` не смотрит, поддерево резолвится, дверь открывается;
 *   - обратно в отдел никого не добавить — резолв при сохранении персонала
 *     фильтрует по `is_active` и молча пропускает неразрешившийся id.
 *
 * Здесь стояло обратное: подтверждение обещало, что «удаление снимет привязку».
 * Это ровно то обещание, которого система не выполняет, и цена ошибки лежит на
 * проходной — дверь после «удаления» пускает тех же самых людей, и заметить это
 * можно только там. Поэтому подтверждение теперь говорит, что произойдёт на
 * самом деле, а числа под отделом называет как то, что ОСТАНЕТСЯ, а не как то,
 * что отвяжется.
 *
 * Настоящего расформирования у кабинета нет: операция в `sv_control` есть
 * (`department_repo::delete` чистит привязки и обнуляет `devices.department_id`),
 * но наружу через гейтвей не выведена. Это задача на бэкенд, а не правка кнопки.
 */
const styles = StyleSheet.create({
  form: {gap: spacing(4)},
  field: {gap: spacing(2)},
  actions: {gap: spacing(2), marginTop: spacing(2)},
});

export function DepartmentForm({
  companyId,
  department,
  visible,
  onClose,
  onCreated,
  onDeleted,
  filedUnder,
}: {
  companyId: string;
  /** Present ⇒ edit; absent ⇒ create. */
  department?: Department;
  visible: boolean;
  onClose: () => void;
  /**
   * Fires with the new department on a successful CREATE only (never on edit), before
   * onClose. Lets a caller that opened this to fill its own gap — the personnel form's
   * department picker — select what the user just made instead of hunting for it in the
   * refreshed list.
   */
  onCreated?: (created: Department) => void;
  /**
   * Отдел удалён. Вызывается ПЕРЕД `onClose`, потому что вызывающий обычно
   * уходит с экрана: остаться на карточке того, чего больше нет, — это пустые
   * запросы и заголовок, который врёт.
   */
  onDeleted?: () => void;
  /**
   * Сколько всего числится под отделом, чтобы подтверждение назвало это вслух.
   * Опущено — подтверждение остаётся коротким: считать нечего, и придумывать
   * нули не за что.
   */
  filedUnder?: {personnel: number; devices: number};
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const editing = !!department;
  const [name, setName] = useState(department?.name ?? '');
  const [type, setType] = useState<string>(
    department?.department_type ?? DEFAULT_DEPARTMENT_TYPE,
  );
  const [code, setCode] = useState(department?.code ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidate = () =>
    qc.invalidateQueries({queryKey: ['departments', companyId]});

  const save = useMutation({
    meta: {toast: 'toast.departmentSaved'},
    mutationFn: () => {
      const input = {name: name.trim(), department_type: type, code: code.trim()};
      return editing
        ? updateDepartment(companyId, department.id, input)
        : createDepartment(companyId, input);
    },
    onSuccess: async saved => {
      await invalidate();
      if (!editing) {
        onCreated?.(saved);
      }
      onClose();
    },
  });

  const remove = useMutation({
    meta: {toast: 'toast.departmentDeleted'},
    mutationFn: () => deleteDepartment(companyId, department!.id),
    onSuccess: async () => {
      await invalidate();
      // Проекция в sv_control обновляется раз в несколько минут
      // (`DEPT_PROJECTION_INTERVAL_SECS`, умолчание 300), поэтому сразу после
      // удаления `/control/departments` ещё отдаёт отдел. Сбрасываем ключ всё
      // равно — он перечитается, когда проекция дойдёт, — но на исчезновение
      // строки в тот же миг рассчитывать нельзя, и экран об этом предупреждает.
      await qc.invalidateQueries({queryKey: ['control-departments', companyId]});
      onDeleted?.();
      onClose();
    },
  });

  /** Есть ли под отделом кто-то, чью привязку удаление снимет. */
  const filedCount = (filedUnder?.personnel ?? 0) + (filedUnder?.devices ?? 0);

  const nameValid = name.trim().length > 0;
  const busy = save.isPending || remove.isPending;

  // Until 2026-08-11 a taken code answered 500 and this showed as "что-то пошло не
  // так". It is a 409 now, and it names the department holding the code — which
  // belongs at the Code field, not in a banner under the sheet's buttons. An EMPTY
  // code is a supported state, not a workaround: the uniqueness index went partial
  // the same day, so any number of departments may have none.
  const codeTaken = fieldConflict(save.error, 'department');

  return (
    <Sheet
      visible={visible}
      onClose={busy ? () => {} : onClose}
      title={t(editing ? 'department.editTitle' : 'department.createTitle')}
      testID="department-form">
      <View style={styles.form}>
        <AppInput
          label={t('department.nameLabel')}
          value={name}
          onChangeText={setName}
          placeholder={t('department.namePlaceholder')}
          autoFocus={!editing}
        />

        <View style={styles.field}>
          <AppText variant="label">{t('department.typeLabel')}</AppText>
          <ChoiceChips
            options={DEPARTMENT_TYPES.map(dt => ({
              value: dt,
              label: t(departmentTypeLabelKey(dt)),
            }))}
            value={type}
            onChange={setType}
            disabled={busy}
          />
        </View>

        <AppInput
          label={t('department.codeLabel')}
          value={code}
          onChangeText={setCode}
          placeholder={t('department.codePlaceholder')}
          autoCapitalize="characters"
          error={
            codeTaken ? t('department.codeTaken', {code: codeTaken.value}) : undefined
          }
          hint={t('department.codeHint')}
          testID="department-code"
        />

        {save.isError && !codeTaken ? (
          <Banner tone="error" title={messageOf(save.error, t('errors.unknown'))} />
        ) : null}
        {remove.isError ? (
          <Banner tone="error" title={messageOf(remove.error, t('errors.unknown'))} />
        ) : null}

        <View style={styles.actions}>
          <AppButton
            title={t(editing ? 'actions.save' : 'department.create')}
            onPress={() => save.mutate()}
            loading={save.isPending}
            disabled={!nameValid || busy}
          />
          {editing ? (
            confirmDelete ? (
              <>
                {/* Названо здесь, а не в баннере наверху листа: подтверждение
                    читают перед нажатием, а до верха листа глаз уже не идёт. */}
                {/*
                  Показывается ВСЕГДА, а не только когда под отделом кто-то есть:
                  «уйдёт из списков, но не расформируется» — правда и про пустой
                  отдел, а состав меняет лишь то, чью судьбу надо назвать поимённо.
                */}
                <Banner
                  tone="warning"
                  title={t('department.deleteTruthTitle')}
                  message={
                    filedCount > 0
                      ? `${t('department.deleteFiled', {
                          personnel: filedUnder?.personnel ?? 0,
                          devices: filedUnder?.devices ?? 0,
                        })}. ${t('department.deleteTruthBody')}`
                      : t('department.deleteTruthBody')
                  }
                  testID="department-delete-warning"
                />
                <AppButton
                  title={t('department.confirmDelete')}
                  tone="destructive"
                  onPress={() => remove.mutate()}
                  loading={remove.isPending}
                  disabled={busy}
                  testID="department-delete-confirm"
                />
                {/* Выход из подтверждения — и НЕ «Отмена»: ниже уже стоит кнопка с
                    этим словом, и она закрывает лист целиком. Два одинаковых
                    «Отмена» подряд заставляют выбирать между ними наугад. */}
                <AppButton
                  title={t('department.deleteAbort')}
                  tone="ghost"
                  onPress={() => setConfirmDelete(false)}
                  disabled={busy}
                  testID="department-delete-cancel"
                />
              </>
            ) : (
              <AppButton
                title={t('actions.delete')}
                tone="ghost"
                icon="trash"
                onPress={() => setConfirmDelete(true)}
                disabled={busy}
                testID="department-delete"
              />
            )
          ) : null}
          <AppButton
            title={t('actions.cancel')}
            tone="neutral"
            onPress={onClose}
            disabled={busy}
          />
        </View>
      </View>
    </Sheet>
  );
}
